<template>
  <div ref="wrapper" class="adn-button-dropdown">
    <AdnActionButton :state="selected ? 'selected' : 'default'" @click.stop="toggle">
      {{ label }}
      <span v-if="selectionCount" class="adn-button-dropdown__count">({{ selectionCount }})</span>
      <span
        class="adn-icon adn-icon--navigation-direction-down adn-button-dropdown__icon"
        :class="{ 'adn-button-dropdown__icon--up': open }"
      ></span>
    </AdnActionButton>

    <Teleport to="body">
      <Transition>
        <div
          v-if="open"
          ref="content"
          class="adn-button-dropdown__content"
          :style="{ top: position.top + 'px', left: position.left + 'px' }"
          @click.stop
        >
          <slot></slot>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import AdnActionButton from '../AdnActionButton/AdnActionButton.vue'

const defaultPadding = 10

defineProps({
  label: {
    type: String,
    default: '',
  },
  selected: {
    type: Boolean,
    default: false,
  },
  selectionCount: {
    type: Number,
    default: null,
  },
})

const emit = defineEmits<(e: 'toggle', value: boolean) => void>()
const open = ref(false)

const content = ref<HTMLElement | null>(null)
const wrapper = ref<HTMLElement | null>(null)
const position = ref({ top: 0, left: 0 })

const setPosition = () => {
  if (wrapper.value) {
    const rect = wrapper.value.getBoundingClientRect()
    const contentWidth = content.value?.getBoundingClientRect().width || 0
    const contentRight = content.value?.getBoundingClientRect().right || 0
    let left = rect.left
    if (rect.left + contentWidth > window.innerWidth) {
      left = rect.right - contentWidth - defaultPadding
    }
    if (rect.right - contentWidth < 0) {
      left = defaultPadding
    }
    if (contentRight > window.innerWidth) {
      left = window.innerWidth - contentWidth - defaultPadding
    }
    position.value = {
      top: rect.top + wrapper.value.clientHeight + window.scrollY + defaultPadding,
      left,
    }
  }
}

const toggle = () => {
  open.value = !open.value
  setPosition()
  emit('toggle', open.value)
}

const handleClickOutside = (e: MouseEvent) => {
  if (content.value && !content.value.contains(e.target as Node)) {
    open.value = false
  }
}

watch(
  () => open.value,
  async (value) => {
    if (value) {
      setTimeout(setPosition)
      // close all other dropdowns before listening for click outside
      wrapper.value?.click()
      await nextTick()
      document.addEventListener('click', handleClickOutside)
    } else {
      document.removeEventListener('click', handleClickOutside)
    }
  },
)

onMounted(() => {
  window.addEventListener('resize', setPosition)
})

onUnmounted(() => {
  window.removeEventListener('resize', setPosition)
})
</script>

<style lang="scss" scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

.adn-button-dropdown {
  position: relative;
  min-width: max-content;

  &__content {
    width: max-content;
    position: absolute;
    z-index: 1;
    padding: var(--spacing-3);
    background-color: var(--color-white);
    border-radius: var(--border-radius-m);
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 20%);
  }

  &__icon {
    transform: rotate(0);
    transition: transform 0.35s ease-in-out;
    color: var(--idf-color-neutral-600);

    &--up {
      transform: rotate(-180deg);
    }
  }

  :deep(.adn-action-button-selected) {
    .adn-button-dropdown__icon {
      color: white;
    }
  }
}
</style>
